import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes";
import { apiFetchJson } from "../../utils/http";
import { getActorType, getClaims } from "../../utils/auth";

const PAGE_SIZE = 500;
const ESTADO_CLIENTE_ACTIVO_ID = 11;
const DEBOUNCE_MS = 300;

export default function ClienteAutocomplete({
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar cliente...",
  estadoId = null,
}) {
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const clientePrecargadoRef = useRef(false);

  const esActorCliente = String(getActorType() || "").toUpperCase() === "CLIENTE";

  // ============================
  //        ESTADOS
  // ============================
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================
  //   PRECARGAR CLIENTE DEL TOKEN
  // ============================
  useEffect(() => {
    const actorType = String(getActorType() || "").toUpperCase();
    const claims = getClaims();
    const clientId = claims?.clientId;

    if (actorType !== "CLIENTE") return;
    if (!clientId) return;
    if (value) return;
    if (clientePrecargadoRef.current) return;

    clientePrecargadoRef.current = true;

    const cargarClienteDelToken = async () => {
      try {
        const data = await apiFetchJson(apiRoutes.clientes.detail(clientId), true);

        const raw = data?.cliente || data?.item || data;

        const clienteNormalizado = {
          ...raw,
          id: raw?.id ?? clientId,
          codigo: raw?.codigo ?? raw?.codigoCliente ?? raw?.clienteCodigo ?? "",
          nombre: raw?.nombre ?? raw?.nombreCliente ?? raw?.clienteNombre ?? "",
          documento:
            raw?.documento ??
            raw?.ruc ??
            raw?.descricaoRuc ??
            raw?.numeroDocumento ??
            "",
        };

        onChange(clienteNormalizado);
        setInputText(clienteNormalizado.nombre || "");
      } catch (error) {
        console.error("No se pudo cargar el cliente del token", error);
      }
    };

    cargarClienteDelToken();
  }, [value, onChange]);

  // ============================
  //   SINCRONIZAR VALUE → INPUT
  // ============================
  useEffect(() => {
    if (value && value.nombre) {
      setInputText(value.nombre);
    }
    if (!value) {
      setInputText("");
    }
  }, [value]);

  // ============================
  //   CLICK AFUERA → CERRAR
  // ============================
  useEffect(() => {
    const handleOutside = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ============================
  //        FETCH
  // ============================
  const fetchClientes = async ({ pageIndex = 0, append = false, query }) => {
    if (loading) return;

    setLoading(true);

    const url = apiRoutes.clientes.autocompleteUrl({
      q: query,
      page: pageIndex,
      size: PAGE_SIZE,
      tipo: "cliente",
      browse: query === "",
      estadoId: estadoId ?? undefined,
    });

    try {
      const response = await apiFetchJson(url, true);
      const responseItems = response?.items || [];

      const more =
        typeof response?.hasMore === "boolean"
          ? response.hasMore
          : responseItems.length === PAGE_SIZE;

      setItems((prev) => (append ? [...prev, ...responseItems] : responseItems));
      setHasMore(more);
      setPage(pageIndex);
    } catch {
      if (!append) {
        setItems([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================
  //        INPUT (con debounce)
  // ============================
  const handleInputChange = (event) => {
    const valueText = event.target.value;
    setInputText(valueText);
    setOpen(true);

    // solo limpiamos selección si queda vacío
    if (value && valueText.trim() === "") {
      onChange(null);
    }

    // debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchClientes({
        pageIndex: 0,
        append: false,
        query: valueText.trim(),
      });
    }, DEBOUNCE_MS);
  };

  const handleFocus = () => {
    setOpen(true);

    // browse inicial (sin debounce)
    if (items.length === 0 && !loading) {
      fetchClientes({
        pageIndex: 0,
        append: false,
        query: inputText.trim(),
      });
    }
  };

  // ============================
  //     SCROLL INFINITO
  // ============================
  const handleScroll = (event) => {
    const element = event.currentTarget;
    const nearBottom =
      element.scrollTop + element.clientHeight >= element.scrollHeight - 40;

    if (nearBottom && hasMore && !loading) {
      fetchClientes({
        pageIndex: page + 1,
        append: true,
        query: inputText.trim(),
      });
    }
  };

  // ============================
  //      SELECCIONAR
  // ============================
  const handleSelect = (cliente) => {
    onChange(cliente);
    setInputText(cliente.nombre ?? "");
    setOpen(false);
  };

  // ============================
  //        LIMPIAR
  // ============================
  const clearSelection = () => {
    onChange(null);
    setInputText("");
    setItems([]);
    setOpen(false);
  };

  // ============================
  //        RENDER
  // ============================
  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        className="input"
        disabled={disabled || esActorCliente}
        placeholder={placeholder}
        value={inputText}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />

      {value && !esActorCliente && (
        <button
          type="button"
          onClick={clearSelection}
          className="absolute right-3 top-2 text-gray-400"
          title="Limpiar"
        >
          ×
        </button>
      )}

      {open && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
          onScroll={handleScroll}
        >
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">
              Cargando clientes...
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">Sin resultados.</div>
          )}

          {items.map((cliente) => (
            <button
              key={cliente.id}
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
              onClick={() => handleSelect(cliente)}
            >
              {cliente.codigo && (
                <div className="font-mono text-xs text-gray-700">
                  {cliente.codigo}
                </div>
              )}

              <div className="text-sm font-medium">{cliente.nombre}</div>

              {cliente.documento && (
                <div className="text-xs text-gray-500">
                  Doc: {cliente.documento}
                </div>
              )}
            </button>
          ))}

          {loading && items.length > 0 && (
            <div className="px-3 py-2 text-gray-400 text-xs">
              Cargando más…
            </div>
          )}
        </div>
      )}
    </div>
  );
}